import React from 'react';
import { Box, Typography, Stack, Paper, IconButton, Chip, Slider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { HeroVideo } from '../types';
import { formatTime, isHlsUrl } from '../utils/video-utils';

interface HeroVideoPlayerProps {
  heroVideo: HeroVideo;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoUrl: string;
  isPreviewPlaying: boolean;
  actualVideoDuration: number | null;
  onRemove: () => void;
  onPausePreview: () => void;
  onPlayPreview: () => void;
  onLoopRangeChange: (event: Event, newValue: number | number[]) => void;
  onVideoTimeUpdate: () => void;
  onLoadedMetadata: () => void;
  onEnded: () => void;
  getEffectiveDuration: () => number;
}

export function HeroVideoPlayer({
  heroVideo,
  videoRef,
  videoUrl,
  isPreviewPlaying,
  onRemove,
  onPausePreview,
  onPlayPreview,
  onLoopRangeChange,
  onVideoTimeUpdate,
  onLoadedMetadata,
  onEnded,
  getEffectiveDuration,
}: HeroVideoPlayerProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      {/* Video Header with Title and Close */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {heroVideo.title}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.secondary">
              {heroVideo.duration}
            </Typography>
            {heroVideo.channelName && (
              <Chip label={heroVideo.channelName} size="small" sx={{ height: 18, fontSize: 10 }} />
            )}
          </Stack>
        </Box>
        <IconButton
          size="small"
          onClick={() => {
            onPausePreview();
            onRemove();
          }}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Video Player Preview */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 480,
          aspectRatio: '16/9',
          bgcolor: 'black',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
        }}
      >
        {videoUrl ? (
          <Box
            component="video"
            ref={videoRef}
            src={isHlsUrl(videoUrl) ? undefined : videoUrl}
            poster={heroVideo.thumbnail}
            playsInline
            onLoadedMetadata={onLoadedMetadata}
            onTimeUpdate={onVideoTimeUpdate}
            onEnded={onEnded}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            muted
          />
        ) : (
          <Box
            component="img"
            src={heroVideo.thumbnail}
            alt={heroVideo.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
        {/* Play/Pause Overlay Button */}
        {videoUrl && (
          <IconButton
            onClick={isPreviewPlaying ? onPausePreview : onPlayPreview}
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
            }}
          >
            {isPreviewPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        )}
        {/* Loop indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            bgcolor: 'rgba(0,0,0,0.6)',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          Loop: {formatTime(heroVideo.loopStart)} – {formatTime(heroVideo.loopEnd)}
        </Box>
      </Box>

      {/* Loop Interval Controls */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Välj loop-intervall
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={[heroVideo.loopStart, heroVideo.loopEnd]}
            onChange={onLoopRangeChange}
            min={0}
            max={Math.max(getEffectiveDuration(), 1)}
            step={0.5}
            valueLabelDisplay="on"
            valueLabelFormat={formatTime}
            disableSwap
            marks={[
              { value: 0, label: '0:00' },
              {
                value: Math.max(getEffectiveDuration(), 1),
                label: formatTime(Math.max(getEffectiveDuration(), 1)),
              },
            ]}
            sx={{
              width: '100%',
              '& .MuiSlider-thumb[data-index="0"]': {
                color: 'primary.main',
              },
              '& .MuiSlider-thumb[data-index="1"]': {
                color: 'error.main',
              },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Längd: {formatTime(heroVideo.loopEnd - heroVideo.loopStart)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
